// Write operations used by the UI. Keeping them here (rather than inside
// components) means every screen logs events the same way.

import { completeCare, generateCarePlan } from '../domain/care.ts';
import { today } from '../domain/time.ts';
import type { CareItem, EventDataMap, EventSource, EventType, Household, LogEvent, Profile } from '../domain/types.ts';
import { devicePrefs } from './device.ts';
import { uid } from './ids.ts';
import { data } from './index.ts';
import { DEFAULT_HOUSEHOLD, DEFAULT_PROFILE } from './seed.ts';

export function currentPerson(): string | undefined {
  return devicePrefs().personId;
}

export function logEvent<T extends EventType>(
  type: T,
  eventData: EventDataMap[T],
  opts: { at?: number; end?: number; by?: string; note?: string; source?: EventSource } = {},
): LogEvent<T> {
  const event = {
    id: uid(),
    type,
    at: opts.at ?? Date.now(),
    ...(opts.end !== undefined ? { end: opts.end } : {}),
    by: opts.by ?? currentPerson(),
    ...(opts.note ? { note: opts.note } : {}),
    source: opts.source ?? 'app',
    data: eventData,
  } as LogEvent<T>;
  data().store.put('events', event as LogEvent);
  return event;
}

export function updateEvent(event: LogEvent): void {
  data().store.put('events', event);
}

export function deleteEvent(id: string): void {
  data().store.remove('events', id);
}

/** Start a nap now (closing any nap left open by mistake). */
export function startSleep(at = Date.now()): LogEvent<'sleep'> {
  for (const open of openDurations('sleep')) updateEvent({ ...open, end: Math.max(open.at + 60_000, at - 1) });
  return logEvent('sleep', {}, { at });
}

/** Wake up: closes every open nap (two devices may each have started one). */
export function endSleep(at = Date.now()): LogEvent<'sleep'> | undefined {
  const open = openDurations('sleep');
  if (!open.length) return undefined;
  const ended = open.map((o) => ({ ...o, end: Math.max(at, o.at + 60_000) }));
  ended.forEach(updateEvent);
  return ended.sort((a, b) => a.at - b.at)[0];
}

/** Start a timed activity (walk, play, alone time). */
export function startTimed(type: 'walk' | 'play' | 'alone', at = Date.now()) {
  const open = openDuration(type);
  if (open) return open;
  return logEvent(type, {} as EventDataMap[typeof type], { at });
}

export function endTimed(type: 'walk' | 'play' | 'alone', at = Date.now()) {
  const open = openDurations(type);
  if (!open.length) return undefined;
  const ended = open.map((o) => ({ ...o, end: Math.max(at, o.at + 60_000) }) as LogEvent);
  ended.forEach(updateEvent);
  return ended[0];
}

function openDurations<T extends 'sleep' | 'walk' | 'play' | 'alone'>(type: T): LogEvent<T>[] {
  const now = Date.now();
  return data()
    .store.list('events')
    .filter((e) => e.type === type && e.end === undefined && e.at <= now && !isPendingAuto(e))
    .sort((a, b) => b.at - a.at) as LogEvent<T>[];
}

export function openDuration<T extends 'sleep' | 'walk' | 'play' | 'alone'>(type: T): LogEvent<T> | undefined {
  const now = Date.now();
  const open = data()
    .store.list('events')
    .filter((e) => e.type === type && e.end === undefined && e.at <= now && !isPendingAuto(e))
    .sort((a, b) => b.at - a.at)[0];
  return open as LogEvent<T> | undefined;
}

export function saveProfile(patch: Partial<Profile>): Profile {
  const { store } = data();
  const current = { ...DEFAULT_PROFILE, ...store.get('profile', 'arlo') };
  const next: Profile = { ...current, ...patch, food: { ...current.food, ...patch.food } };
  store.put('profile', next);
  return next;
}

export function saveHousehold(patch: Partial<Household>): Household {
  const { store } = data();
  const next: Household = { ...DEFAULT_HOUSEHOLD, ...store.get('settings', 'household'), ...patch };
  store.put('settings', next);
  return next;
}

/**
 * Create (or refresh) the generated care plan. Existing items the household
 * has touched (done/edited dates) are kept; only missing ones are added.
 */
export function ensureCarePlan(): number {
  const { store } = data();
  const profile = store.get('profile', 'arlo');
  const household = store.get('settings', 'household') ?? DEFAULT_HOUSEHOLD;
  if (!profile?.dob || !profile.homeDate) return 0;
  const plan = generateCarePlan({ dob: profile.dob, homeDate: profile.homeDate, region: household.region });
  const existing = store.list('care');
  const templates = new Set(existing.map((c) => c.template).filter(Boolean));
  const missing = plan.filter((p) => !templates.has(p.template) && !store.get('care', p.id));
  if (missing.length) store.putMany('care', missing, { weak: true });
  return missing.length;
}

/** Re-date generated items that haven't been done or edited, e.g. after the DOB changes. */
export function redateCarePlan(): void {
  const { store } = data();
  const profile = store.get('profile', 'arlo');
  const household = store.get('settings', 'household') ?? DEFAULT_HOUSEHOLD;
  if (!profile?.dob || !profile.homeDate) return;
  const plan = generateCarePlan({ dob: profile.dob, homeDate: profile.homeDate, region: household.region });
  const updates: CareItem[] = [];
  for (const p of plan) {
    const cur = store.get('care', p.id);
    if (cur && !cur.done && !cur.edited && cur.template === p.template && cur.due !== p.due) updates.push({ ...cur, due: p.due });
  }
  if (updates.length) store.putMany('care', updates);
  ensureCarePlan();
}

export function markCareDone(item: CareItem, doneOn = today()): CareItem | undefined {
  const { store } = data();
  const profile = store.get('profile', 'arlo') ?? DEFAULT_PROFILE;
  const by = currentPerson();
  // Deterministic id for the follow-up so two devices completing the same
  // item offline converge on one next occurrence instead of two.
  const series = item.template ?? item.id.split('@')[0];
  const { done, next } = completeCare(item, doneOn, by, profile, () => '');
  if (next) next.id = `${series}@${next.due}`;
  store.put('care', done);
  if (next) store.put('care', next);
  logEvent('care', { careId: item.id, title: item.title }, { at: Date.now(), by });
  return next;
}

/**
 * Undo markCareDone: reopen the item, remove the next occurrence it booked
 * (unless someone has since changed or done it) and its "done" log entry.
 */
export function reopenCare(id: string): void {
  const { store } = data();
  const item = store.get('care', id);
  if (!item?.done) return;
  const profile = store.get('profile', 'arlo') ?? DEFAULT_PROFILE;
  const series = item.template ?? item.id.split('@')[0];
  const { next } = completeCare(item, item.done, item.doneBy, profile, () => '');
  if (next) {
    const n = store.get('care', `${series}@${next.due}`);
    if (n && !n.done && !n.edited) store.remove('care', n.id);
  }
  const { done: _d, doneBy: _b, ...open } = item;
  store.put('care', open);
  const logged = store
    .list('events')
    .filter((e) => e.type === 'care' && e.data.careId === id)
    .sort((a, b) => b.at - a.at)[0];
  if (logged) store.remove('events', logged.id);
}

export function saveCare(item: CareItem): void {
  data().store.put('care', item);
}

export function newCare(partial: Omit<CareItem, 'id'>): CareItem {
  const item = { id: uid(), ...partial };
  data().store.put('care', item);
  return item;
}

/** An outing detected from a phone that nobody has confirmed Arlo was on. */
export function isPendingAuto(e: LogEvent): boolean {
  return e.type === 'walk' && e.data.auto === 'pending';
}

export function confirmAutoWalk(e: LogEvent<'walk'>, withArlo: boolean): void {
  if (withArlo) updateEvent({ ...e, data: { ...e.data, auto: 'confirmed' }, note: e.note ?? 'Detected from phone location' });
  else deleteEvent(e.id);
}
